import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import * as SliderRadix from '@radix-ui/react-slider';
import styled, { css } from 'styled-components';
import { color } from "../../../../themes/mixins/Fonts.js";
var DEFAULT_STEP_INCREMENT = 1;
export var Slider = function Slider(_ref) {
  var LabelText = _ref.LabelText,
    rangeMin = _ref.rangeMin,
    rangeMax = _ref.rangeMax,
    step = _ref.step,
    selectedValue = _ref.selectedValue,
    selectedSecondValue = _ref.selectedSecondValue,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    formatter = _ref.formatter,
    handleChange = _ref.handleChange,
    handleCommittedChange = _ref.handleCommittedChange;
  var _useState = useState(function () {
      return handleSelectedValues(selectedValue, selectedSecondValue);
    }),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var _useState3 = useState(function () {
      return handleSelectedValues(selectedValue, selectedSecondValue);
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    committedValue = _useState4[0],
    setCommittedValue = _useState4[1];
  var handleOnChange = function handleOnChange(val) {
    var formattedValue = handleSelectedValues(val[0], val[1]);
    setValue(formattedValue);
    if (handleChange) {
      handleChange(formattedValue);
    }
  };
  var handleOnChangeCommitted = function handleOnChangeCommitted(val) {
    setCommittedValue(value);
    if (handleCommittedChange) {
      handleCommittedChange(handleSelectedValues(val[0], val[1]));
    }
  };

  /* 
    The Radix slider has some problem with the onValueCommit event on Chrome browser.
    The event is not always triggered so we have to do this workaround to trigger it manually.
  */
  var handleCommittedValueDiscrepancy = function handleCommittedValueDiscrepancy() {
    if (checkCommittedValueDiscrepancy()) {
      handleOnChangeCommitted(Array.isArray(value) ? value : [value]);
    }
  };
  var checkCommittedValueDiscrepancy = function checkCommittedValueDiscrepancy() {
    if (Array.isArray(committedValue)) {
      return committedValue[0] !== value[0] || committedValue[1] !== value[1];
    }
    return committedValue !== value;
  };
  useEffect(function () {
    setValue(handleSelectedValues(selectedValue, selectedSecondValue));
    setCommittedValue(handleSelectedValues(selectedValue, selectedSecondValue));
  }, [selectedValue, selectedSecondValue]);
  return /*#__PURE__*/React.createElement(SliderWrapper, null, LabelText && (typeof LabelText === 'string' ? /*#__PURE__*/React.createElement(FilterLabel, null, LabelText) : /*#__PURE__*/React.createElement(LabelText, null)), /*#__PURE__*/React.createElement(ValuesContainer, null, /*#__PURE__*/React.createElement(ValueContainer, {
    $disabled: disabled
  }, Array.isArray(value) ? formatter(value[0]) : formatter(rangeMin)), /*#__PURE__*/React.createElement(ValueContainer, {
    $disabled: disabled
  }, Array.isArray(value) ? formatter(value[1]) : formatter(value))), /*#__PURE__*/React.createElement(SliderRoot, {
    value: Array.isArray(value) ? value : [value],
    min: rangeMin,
    max: rangeMax,
    disabled: disabled,
    step: step || DEFAULT_STEP_INCREMENT,
    onValueChange: handleOnChange,
    onValueCommit: handleOnChangeCommitted,
    onLostPointerCapture: handleCommittedValueDiscrepancy
  }, /*#__PURE__*/React.createElement(SliderTrack, null, /*#__PURE__*/React.createElement(SliderRange, null)), /*#__PURE__*/React.createElement(SliderThumb, null), !!selectedSecondValue && /*#__PURE__*/React.createElement(SliderThumb, null)));
};
var handleSelectedValues = function handleSelectedValues(firstValue, secondValue) {
  if (secondValue) {
    return [firstValue, secondValue];
  }
  return firstValue;
};
var SliderWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Slider__SliderWrapper",
  componentId: "sc-lsb7pl-0"
})(["background-color:", ";"], function (_ref2) {
  var theme = _ref2.theme;
  return theme.commons.colors.gray0;
});
var FilterLabel = /*#__PURE__*/styled.div.withConfig({
  displayName: "Slider__FilterLabel",
  componentId: "sc-lsb7pl-1"
})(["", ";font-family:", ";color:", ";", ";"], function (props) {
  return props.theme.mixins.font('Title4');
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.commons.font.family;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.commons.colors.gray800;
}, function (p) {
  return p.theme.mixins.margin({
    bottom: 2
  });
});
var ValuesContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Slider__ValuesContainer",
  componentId: "sc-lsb7pl-2"
})(["display:flex;flex-wrap:wrap;justify-content:space-between;width:100%;", ";"], function (p) {
  return p.theme.mixins.margin({
    bottom: 1.5
  });
});
var ValueContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Slider__ValueContainer",
  componentId: "sc-lsb7pl-3"
})(["", ";font-family:", ";", ""], function (props) {
  return props.theme.mixins.font('TextLarge');
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.commons.font.family;
}, function (_ref6) {
  var $disabled = _ref6.$disabled;
  return $disabled ? css(["color:", ";"], function (_ref7) {
    var theme = _ref7.theme;
    return theme.commons.colors.gray200;
  }) : css(["color:", ";"], function (_ref8) {
    var theme = _ref8.theme;
    return theme.commons.colors.gray800;
  });
});
var SliderTrack = /*#__PURE__*/styled(SliderRadix.Track).withConfig({
  displayName: "Slider__SliderTrack",
  componentId: "sc-lsb7pl-4"
})(["background-color:", ";position:relative;flex-grow:1;border-radius:9999px;height:4px;"], color('gray50'));
var SliderRange = /*#__PURE__*/styled(SliderRadix.Range).withConfig({
  displayName: "Slider__SliderRange",
  componentId: "sc-lsb7pl-5"
})(["position:absolute;background-color:", ";border-radius:9999px;height:100%;"], color('primary400'));
var SliderThumb = /*#__PURE__*/styled(SliderRadix.Thumb).withConfig({
  displayName: "Slider__SliderThumb",
  componentId: "sc-lsb7pl-6"
})(["cursor:pointer;display:block;width:20px;height:20px;background-color:", ";box-shadow:rgba(0,0,0,0.02) 0px 2px 1px,rgba(0,0,0,0.12) 0px 2px 6px;border-radius:10px;&:hover{box-shadow:'none';}"], color('gray0'));
var SliderRoot = /*#__PURE__*/styled(SliderRadix.Root).withConfig({
  displayName: "Slider__SliderRoot",
  componentId: "sc-lsb7pl-7"
})(["position:relative;display:flex;align-items:center;user-select:none;touch-action:none;height:26px;", ""], function (_ref9) {
  var disabled = _ref9.disabled;
  return disabled ? css(["", "{background-color:", ";}", "{cursor:default;}"], SliderRange, color('gray200'), SliderThumb) : '';
});